import React, { useState, useEffect } from 'react';
import Truck from 'lucide-react/dist/esm/icons/truck.js';
import { useLanguage } from '@/contexts/LanguageContext';

const buildLocalState = (contact = {}) => ({
  fullName: contact.fullName || contact.name || '',
  phone: contact.phone || '',
  street: contact.street || contact.address_street || '',
  city: contact.city || contact.address_city || ''
});

const DeliveryOptions = ({ withDelivery, freeDelivery = false, onDeliveryChange, contact = {}, onContactChange }) => {
  const { t, language } = useLanguage();
  const [mode, setMode] = useState(withDelivery ? 'delivery' : 'pickup');
  const [local, setLocal] = useState(buildLocalState(contact));

  useEffect(() => {
    setMode(withDelivery ? 'delivery' : 'pickup');
  }, [withDelivery]);

  useEffect(() => {
    setLocal(buildLocalState(contact));
  }, [contact]);

  const handleModeChange = (m) => {
    setMode(m);
    const isDelivery = m === 'delivery';
    if (typeof onDeliveryChange === 'function') onDeliveryChange(isDelivery);
  };

  const handleFieldBlur = () => {
    if (typeof onContactChange === 'function') {
      onContactChange({
        ...(contact || {}),
        ...local,
        name: local.fullName || contact?.name || ''
      });
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{t('deliveryOptions')}</h3>
        <div
          className="flex w-full max-w-[240px] rounded-full bg-gray-100 p-1"
          role="group"
          aria-label={language === 'he' ? 'אופן קבלת ההזמנה' : 'Order collection method'}
        >
          <button
            type="button"
            onClick={() => handleModeChange('delivery')}
            aria-pressed={mode === 'delivery'}
            className={`min-h-10 flex-1 rounded-full px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 active:translate-y-px motion-reduce:transform-none ${mode === 'delivery' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-white/70'}`}
          >
            {language === 'he' ? 'משלוח' : t('addDelivery')}
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('pickup')}
            aria-pressed={mode === 'pickup'}
            className={`min-h-10 flex-1 rounded-full px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 active:translate-y-px motion-reduce:transform-none ${mode === 'pickup' ? 'bg-white text-gray-700 shadow-sm' : 'text-gray-700 hover:bg-white/70'}`}
          >
            {language === 'he' ? 'איסוף עצמי' : 'Pickup'}
          </button>
        </div>
      </div>

      {/* Show pickup address when pickup selected; otherwise render delivery form */}
      {mode === 'pickup' ? (
        <div className="text-sm text-gray-700 mb-3">{t('pickupAddress')}</div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'he' ? 'שם מלא' : 'Full name'}</label>
              <input
                className="w-full border rounded px-3 py-2 text-sm"
                value={local.fullName}
                onChange={(e) => setLocal(s => ({ ...s, fullName: e.target.value }))}
                onBlur={handleFieldBlur}
                placeholder={language === 'he' ? 'שם מלא' : 'Full name'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'he' ? 'טלפון' : 'Phone'}</label>
              <input
                className="w-full border rounded px-3 py-2 text-sm"
                value={local.phone}
                onChange={(e) => setLocal(s => ({ ...s, phone: e.target.value }))}
                onBlur={handleFieldBlur}
                placeholder={language === 'he' ? '05x-xxxxxxx' : '05x-xxxxxxx'}
              />
            </div>
          </div>

          <div>
            <label htmlFor="delivery-street" className="block text-sm font-medium text-gray-700 mb-1">
              {language === 'he' ? 'רחוב ומס בית' : 'Street & house number'} <span className="text-red-500">*</span>
            </label>
            <input
              id="delivery-street"
              required
              aria-required="true"
              autoComplete="street-address"
              className="w-full border rounded px-3 py-2 text-sm"
              value={local.street}
              onChange={(e) => setLocal(s => ({ ...s, street: e.target.value }))}
              onBlur={handleFieldBlur}
              placeholder={language === 'he' ? 'שם הרחוב ומס בית' : 'Street and house number'}
            />
          </div>

          <div>
            <label htmlFor="delivery-city" className="block text-sm font-medium text-gray-700 mb-1">
              {language === 'he' ? 'עיר' : 'City'} <span className="text-red-500">*</span>
            </label>
            <input
              id="delivery-city"
              required
              aria-required="true"
              autoComplete="address-level2"
              className="w-full border rounded px-3 py-2 text-sm"
              value={local.city}
              onChange={(e) => setLocal(s => ({ ...s, city: e.target.value }))}
              onBlur={handleFieldBlur}
              placeholder={language === 'he' ? 'עיר' : 'City'}
            />
          </div>

          <p className="text-sm text-gray-500 mt-2">
            {freeDelivery
              ? (language === 'he' ? 'משלוח ללא עלות עם הקופון.' : 'Delivery is free with this coupon.')
              : t('deliveryPriceInfo')}
          </p>
        </div>
      )}
    </div>
  );
};

export default DeliveryOptions;
